import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdjustWalletModal from "../wallet/AdjustWalletModal";
import AdjustLoyaltyModal from "../loyalty/AdjustLoyaltyModal";
import { getWalletCustomers } from "../../utils/apiCalls";
import { formatCurrency } from "../../utils/formatCurrency";
import { useSelector } from "react-redux";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import Breadcrumb from "../common/breadcrumb";
import { Container } from "reactstrap";
import { C, tbl, reportingGlobalCSS } from "./reportingStyles";
import { GradientDefs, ChartTooltip, RCard, Badge, EventBadge, ChannelBadge, StatusBadge, Loader, TablePagination, usePagination } from "./ReportingShared";
import { getReportingCustomerDetail, getOrderListing } from "../../utils/apiCalls";
import OrderDetailModal from "../common/OrderDetailModal";
import { canAdjustCustomerBalances, isMerchantScoped, isOperations } from "../../constants/roles";

// ─── Order status pill (order states differ from the voucher StatusBadge) ─
const orderStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "canceled" || s === "cancelled") return { fg: C.coral, bg: C.coralLight };
  if (s === "pending") return { fg: C.orange, bg: C.orangeLight };
  if (s === "delivered" || s === "accepted" || s === "completed") return { fg: C.green, bg: C.greenLight };
  if (s === "shipped" || s === "ready") return { fg: C.blue, bg: C.blueLight };
  return { fg: C.textMed, bg: C.bg };
};
const OrderStatusPill = ({ status }) => {
  const col = orderStatusStyle(status);
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "--";
  return (
    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, color: col.fg, background: col.bg, letterSpacing: 0.3 }}>
      {label}
    </span>
  );
};

// ─── Main Component ──────────────────────────────────────
const CustomerDetailPage = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const userType = localStorage.getItem("userType");
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = isMerchantScoped(userType) ? merchantIdFromStorage : reduxSelectedMerchant;
  const canAdjust = canAdjustCustomerBalances(userType);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all" || !customerId) return;
    setLoading(true);
    try {
      const res = await getReportingCustomerDetail(selectedMerchant, customerId);
      if (res) setData(res);
    } catch (e) { console.error("Failed to fetch customer detail:", e); }
    setLoading(false);
  }, [selectedMerchant, customerId]);

  const [wallet, setWallet] = React.useState(null);
  const [adjustOpen, setAdjustOpen] = React.useState(false);
  const [loyaltyAdjustOpen, setLoyaltyAdjustOpen] = React.useState(false);

  const loadWallet = React.useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all" || !customerId) return;
    const res = await getWalletCustomers({ merchantId: selectedMerchant, userId: customerId, limit: 1 });
    setWallet(res?.rows?.[0] ?? null);
  }, [selectedMerchant, customerId]);

  React.useEffect(() => { loadWallet(); }, [loadWallet]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Customer orders (server-side paginated) ───────────
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLimit, setOrdersLimit] = useState(10);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  // Reset to first page when the customer or merchant changes
  useEffect(() => { setOrdersPage(1); }, [selectedMerchant, customerId]);

  useEffect(() => {
    if (!selectedMerchant || selectedMerchant === "all" || !customerId) return;
    let active = true;
    (async () => {
      setOrdersLoading(true);
      try {
        const res = await getOrderListing(ordersPage, ordersLimit, { customerId, merchant: selectedMerchant });
        if (!active) return;
        setOrders(res?.data?.orders || []);
        setOrdersTotal(res?.data?.meta?.totalRecords || 0);
      } finally {
        if (active) setOrdersLoading(false);
      }
    })();
    return () => { active = false; };
  }, [selectedMerchant, customerId, ordersPage, ordersLimit]);

  const c = data?.customer;
  const ledger = data?.ledger || [];
  const redemptions = data?.redemptions || [];
  const vouchers = data?.vouchers || [];
  const vehicles = data?.vehicles || [];
  const visitFrequency = data?.visitFrequency || [];

  const ledgerPg = usePagination(ledger, 5);
  const redemptionsPg = usePagination(redemptions, 5);
  const vouchersPg = usePagination(vouchers, 5);
  const vehiclesPg = usePagination(vehicles, 5);

  if (!selectedMerchant || selectedMerchant === "all") {
    return (
      <Fragment>
        <Breadcrumb title="Customer Detail" parent="Reporting" />
        <Container fluid>
          <div className="rpt-wrap" style={{ padding: 40, textAlign: "center", color: C.textMed }}>
            <style>{reportingGlobalCSS}</style>
            Please select a merchant.
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title="Customer Detail" parent="Reporting" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {loading || !c ? <Loader /> : (
            <div className="rpt-fade">
              <button onClick={() => navigate("/reporting/customer-loyalty")}
                style={{ background: C.white, border: `1.5px solid ${C.border}`, color: C.text, padding: "8px 18px", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 13, marginBottom: 20, display: "inline-flex", alignItems: "center", gap: 6, boxShadow: C.shadow, transition: "all 0.15s" }}
                onMouseEnter={e => { e.target.style.borderColor = C.coral; e.target.style.color = C.coral; }}
                onMouseLeave={e => { e.target.style.borderColor = C.border; e.target.style.color = C.text; }}>
                ← Back
              </button>

              <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 280px) 1fr", gap: 20 }}>
                {/* Left sidebar */}
                <div>
                  <div className="rpt-lift" style={{ background: C.white, borderRadius: 14, padding: 24, boxShadow: C.shadow, border: `1px solid ${C.border}`, textAlign: "center", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 80, background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, borderRadius: "14px 14px 0 0" }} />
                    <div style={{ position: "relative", zIndex: 1 }}>
                      <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.white, display: "flex", alignItems: "center", justifyContent: "center", margin: "20px auto 0", fontSize: 24, fontWeight: 800, color: C.coral, border: `3px solid ${C.white}`, boxShadow: "0 4px 12px rgba(0, 150, 137,0.2)" }}>
                        {(c.name || "").split(" ").filter(Boolean).map(n => n[0]).join("")}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 12, color: C.text, display: "flex", justifyContent: "center", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        {c.name}
                        {c.active === false && <Badge color={C.coral} bg={C.coralLight}>Inactive</Badge>}
                      </div>
                      <div style={{ color: C.textLight, fontSize: 12, marginTop: 4 }}>{c.phone}</div>
                      <div style={{ color: C.textLight, fontSize: 11, marginTop: 2 }}>Joined {c.joinDate}</div>
                      <div style={{ display: "flex", justifyContent: "space-around", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.borderLight}` }}>
                        <div style={{ textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{c.totalOrders}</div><div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>Orders</div></div>
                        <div style={{ textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 800, color: C.coral }}>{c.totalSpent}</div><div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>SAR</div></div>
                        <div style={{ textAlign: "center" }}><div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{c.avgOrderValue}</div><div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>Avg</div></div>
                      </div>
                    </div>
                  </div>
                  <RCard title="Profile Details" style={{ marginTop: 16 }}>
                    {[
                      ["Fav Branch", c.favBranch], ["Fav Product", c.favProduct],
                      ["Stamps Balance", c.stampsBalance], ["Lifetime Stamps", c.lifetimeStamps],
                      ["Points Balance", c.pointsBalance], ["Lifetime Points", c.lifetimePoints],
                      ["Rewards Balance", c.rewardsBalance], ["Lifetime Rewards", c.lifetimeRewards],
                      ["Last Visit", c.lastVisit],
                    ].map(([l, v]) => (
                      <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${C.borderLight}`, fontSize: 13 }}>
                        <span style={{ color: C.textMed }}>{l}</span><span style={{ fontWeight: 700, color: C.text }}>{v}</span>
                      </div>
                    ))}
                    {/* Only with an active programme: the server needs one to know which balances exist. */}
                    {canAdjust && data?.loyalty && (
                      <button
                        type="button"
                        onClick={() => setLoyaltyAdjustOpen(true)}
                        style={{ marginTop: 12, width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.coral}`, background: C.coralLight, color: C.coralDark, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                      >
                        Adjust loyalty
                      </button>
                    )}
                  </RCard>

                  {wallet && (
                    <RCard title="👛 Customer Wallet" style={{ marginTop: 16 }}>
                      {[
                        ["Purchased", formatCurrency(wallet.purchasedBalance), "money they paid — refundable and transferable"],
                        ["Promotional", formatCurrency(wallet.promotionalBalance), "granted credit — spend only"],
                        ["Total", formatCurrency(wallet.totalBalance), null],
                        ["Lifetime topped up", formatCurrency(wallet.lifetimeToppedUp), null],
                        ["Lifetime spent", formatCurrency(wallet.lifetimeSpent), null],
                      ].map(([l, v, hint]) => (
                        <div key={l} style={{ padding: "9px 0", borderBottom: `1px solid ${C.borderLight}`, fontSize: 13 }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: C.textMed }}>{l}</span>
                            <span style={{ fontWeight: 700, color: C.text }}>{v}</span>
                          </div>
                          {hint && <div style={{ fontSize: 10, color: C.textLight, marginTop: 1 }}>{hint}</div>}
                        </div>
                      ))}
                      {wallet.status !== "active" && (
                        <div style={{ marginTop: 10, padding: 8, background: "#FEF2F2", borderRadius: 6, fontSize: 12, color: "#B91C1C" }}>
                          Wallet is {wallet.status}{wallet.frozenReason ? ` — ${wallet.frozenReason}` : ""}
                        </div>
                      )}
                      {canAdjust && (
                        <button
                          type="button"
                          onClick={() => setAdjustOpen(true)}
                          style={{ marginTop: 12, width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.coral}`, background: C.coralLight, color: C.coralDark, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                        >
                          Adjust balance
                        </button>
                      )}
                    </RCard>
                  )}
                </div>

                {/* Right content */}
                <div>
                  <RCard title="📈 Visit Frequency">
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={visitFrequency}>
                        <GradientDefs />
                        <XAxis dataKey="w" tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="v" fill="url(#gCoralBar)" radius={[8, 8, 0, 0]} name="Visits" barSize={32} />
                      </BarChart>
                    </ResponsiveContainer>
                  </RCard>

                  {/* Loyalty Ledger with pagination */}
                  <RCard title="📒 Loyalty Ledger">
                    {ledger.length > 0 ? <>
                      <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                        <table style={tbl.table}>
                          <thead><tr>{["Date", "Order", "Event", "Delta", "Details"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                          <tbody>{ledgerPg.paged.map(l => (
                            <tr key={l.id} className="rpt-row">
                              <td style={tbl.td}>{l.date}</td>
                              <td style={{ ...tbl.td, color: C.coral, fontWeight: 700 }}>{l.orderId || "—"}</td>
                              <td style={tbl.td}><EventBadge t={l.type} /></td>
                              <td style={{ ...tbl.td, fontWeight: 700, color: String(l.delta).startsWith("-") ? C.coral : String(l.delta) === "0" ? C.textLight : C.green }}>{l.delta}</td>
                              <td style={{ ...tbl.td, color: C.textMed }}>{l.desc}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      <TablePagination total={ledgerPg.total} page={ledgerPg.page} limit={ledgerPg.limit} onPageChange={ledgerPg.setPage} onLimitChange={ledgerPg.setLimit} />
                    </> : <div style={{ color: C.textLight, padding: 16, textAlign: "center" }}>No ledger entries.</div>}
                  </RCard>

                  {/* Redeemed Rewards with pagination */}
                  <RCard title="🎁 Redeemed Rewards">
                    {redemptions.length > 0 ? <>
                      <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                        <table style={tbl.table}>
                          <thead><tr>{["Code", "Product", "Price", "Channel", "Type"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                          <tbody>{redemptionsPg.paged.map(r => (
                            <tr key={r.id} className="rpt-row">
                              <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 12, color: C.textMed }}>{r.code}</td>
                              <td style={{ ...tbl.td, fontWeight: 700 }}>{r.product}</td>
                              <td style={tbl.td}>{r.price} SAR</td>
                              <td style={tbl.td}><ChannelBadge c={r.channel} /></td>
                              <td style={{ ...tbl.td, color: C.textMed }}>{r.rewardType}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      <TablePagination total={redemptionsPg.total} page={redemptionsPg.page} limit={redemptionsPg.limit} onPageChange={redemptionsPg.setPage} onLimitChange={redemptionsPg.setLimit} />
                    </> : <div style={{ color: C.textLight, padding: 16, textAlign: "center" }}>No redemptions.</div>}
                  </RCard>

                  {/* Vouchers with pagination */}
                  <RCard title="🎫 Vouchers">
                    {vouchers.length > 0 ? <>
                      <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                        <table style={tbl.table}>
                          <thead><tr>{["Code", "Product", "Status", "Expires"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                          <tbody>{vouchersPg.paged.map(v => (
                            <tr key={v.code} className="rpt-row">
                              <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 12, color: C.textMed }}>{v.code}</td>
                              <td style={{ ...tbl.td, fontWeight: 700 }}>{v.product}</td>
                              <td style={tbl.td}><StatusBadge s={v.status} /></td>
                              <td style={{ ...tbl.td, color: C.textLight }}>{v.expiresAt}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      <TablePagination total={vouchersPg.total} page={vouchersPg.page} limit={vouchersPg.limit} onPageChange={vouchersPg.setPage} onLimitChange={vouchersPg.setLimit} />
                    </> : <div style={{ color: C.textLight, padding: 16, textAlign: "center" }}>No vouchers.</div>}
                  </RCard>

                  {/* Registered vehicles for car pickup. Read-only: the customer owns
                      this data, and a support question is answered by looking. */}
                  <RCard title="🚗 Vehicles">
                    {vehicles.length > 0 ? <>
                      <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                        <table style={tbl.table}>
                          <thead><tr>{["Plate", "Vehicle", "Nickname", "Added"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                          <tbody>{vehiclesPg.paged.map(v => (
                            <tr key={v.id} className="rpt-row">
                              <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 13, fontWeight: 700, letterSpacing: 1, whiteSpace: "nowrap" }}>
                                {v.plate}
                                {v.isDefault && <span style={{ marginLeft: 8 }}><Badge color={C.coral} bg={C.coralLight}>Default</Badge></span>}
                              </td>
                              <td style={tbl.td}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                                  {v.color?.hex && (
                                    <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 4, background: v.color.hex, border: `1px solid ${C.border}`, flexShrink: 0 }} />
                                  )}
                                  <span style={{ fontWeight: 700 }}>
                                    {[v.color?.name, v.make?.name, v.model?.name].filter(Boolean).join(" ") || "—"}
                                  </span>
                                </span>
                              </td>
                              <td style={{ ...tbl.td, color: C.textMed }}>{v.nickname || "-"}</td>
                              <td style={{ ...tbl.td, color: C.textLight }}>{v.createdAt ? new Date(v.createdAt).toLocaleDateString() : "-"}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      <TablePagination total={vehiclesPg.total} page={vehiclesPg.page} limit={vehiclesPg.limit} onPageChange={vehiclesPg.setPage} onLimitChange={vehiclesPg.setLimit} />
                    </> : <div style={{ color: C.textLight, padding: 16, textAlign: "center" }}>No vehicles registered.</div>}
                  </RCard>

                  {/* Recent Orders (server-side paginated, click to view details) */}
                  <RCard title="🧾 Recent Orders">
                    {ordersLoading ? (
                      <Loader />
                    ) : orders.length > 0 ? (
                      <>
                        <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                          <table style={tbl.table}>
                            <thead>
                              <tr>
                                <th style={tbl.th}>Order ID</th>
                                <th style={tbl.th}>Date</th>
                                <th style={tbl.th}>Branch</th>
                                <th style={{ ...tbl.th, textAlign: "right" }}>Total</th>
                                <th style={tbl.th}>Status</th>
                              </tr>
                            </thead>
                            <tbody>{orders.map(o => (
                              <tr
                                key={o.id}
                                className="rpt-row"
                                style={{ cursor: "pointer" }}
                                title="View order details"
                                onClick={() => { setSelectedOrder({ id: o.id }); setOrderModalOpen(true); }}
                              >
                                <td style={{ ...tbl.td, color: C.coral, fontWeight: 700 }}>
                                  #{o.id}
                                  {o.parentOrderId ? (
                                    <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: C.coralLight, color: C.coral }}>Add-on</span>
                                  ) : null}
                                </td>
                                <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                  {o.createdAt
                                    ? new Date(o.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })
                                    : "—"}
                                </td>
                                <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>{o.Branch?.name || "—"}</td>
                                <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, color: C.text }}>
                                  SAR {Number(o.grandTotal ?? o.productCost ?? 0).toFixed(2)}
                                </td>
                                <td style={tbl.td}><OrderStatusPill status={o.status} /></td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                        <TablePagination total={ordersTotal} page={ordersPage} limit={ordersLimit} onPageChange={setOrdersPage} onLimitChange={setOrdersLimit} />
                      </>
                    ) : (
                      <div style={{ color: C.textLight, padding: 16, textAlign: "center" }}>No orders.</div>
                    )}
                  </RCard>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
      <OrderDetailModal
        selectedOrder={selectedOrder}
        orderModalOpen={orderModalOpen}
        setOrderModalOpen={setOrderModalOpen}
      />
      {wallet && canAdjust && (
        <AdjustWalletModal
          isOpen={adjustOpen}
          toggle={() => setAdjustOpen(false)}
          merchantId={selectedMerchant}
          customer={wallet.user}
          purchasedBalance={wallet.purchasedBalance}
          promotionalBalance={wallet.promotionalBalance}
          // Only a platform admin may mint transferable (purchased) credit. Enforced server-side
          // too — this just stops the option being offered.
          isPlatformAdmin={userType === "admin"}
          // Operations may move promotional credit only, in both directions. Enforced server-side too.
          promotionalOnly={isOperations(userType)}
          onDone={loadWallet}
        />
      )}
      {c && data?.loyalty && canAdjust && (
        <AdjustLoyaltyModal
          isOpen={loyaltyAdjustOpen}
          toggle={() => setLoyaltyAdjustOpen(false)}
          merchantId={selectedMerchant}
          customer={{ id: c.id, firstName: c.name, phoneNo: c.phone }}
          loyalty={data.loyalty}
          balances={{ stampsBalance: c.stampsBalance, rewardsAvailable: c.rewardsBalance, rewardPoints: c.pointsBalance }}
          // Re-reads balances and the ledger, so the new row shows straight away.
          onDone={fetchData}
        />
      )}
    </Fragment>
  );
};

export default CustomerDetailPage;
